import { CSSTransition, TransitionGroup } from 'react-transition-group'
import { CodeDesign } from '../GeneratorSections/CodeDesign/CodeDesign'
import { FC } from 'react'
import { LoggedOutGeneratorSection } from '../LoggedOutCodeGenerator.types'
import { SCROLL_CONTAINER_ID, TRANSITION_DURATION } from '../LoggedOutCodeGenerator.constants'
import { ScanDestination } from '../GeneratorSections/ScanDestination/ScanDestination'
import { Stack } from '@dtx-company/design-system/src'
import { selectDesignFirst } from '@app/code/src/machines/locg/locgSelectors'
import { useGlobalServices } from '@app/common/src/hooks/useGlobalServices'
import { useLOCGSectionTransitionStyles } from './hooks/useLOCGSectionTransitionStyles'
import { useScrollableContainerState } from './hooks/useScrollableContainerState'
import { useSelector } from '@xstate/react'
import dynamic from 'next/dynamic'

const AuthSection = dynamic(
  () => import('../GeneratorSections/AuthSection/AuthSection').then(mod => mod.AuthSection),
  { ssr: false }
)

export const ScrollableContainer: FC = () => {
  const { locgService } = useGlobalServices()
  const { rootContainerRef, scanDestinationRef, codeDesignContainerRef, codeDesignRefs, authRef } =
    useScrollableContainerState()
  const displayScanDestination = useSelector(locgService, state => state.matches('scanDestination'))
  const displayCodeDesign = useSelector(locgService, state => state.matches('codeDesign'))
  const displayAuthForm = useSelector(locgService, state => state.matches('auth'))
  const designFirst = useSelector(locgService, selectDesignFirst)

  const { scanDestinationSectionStyles, codeDesignSectionStyles, authSectionStyles } =
    useLOCGSectionTransitionStyles(
      rootContainerRef.current ? `${rootContainerRef.current.clientHeight}px` : '100vh'
    )

  return (
    <Stack
      sx={{
        height: '100%',
        overflowY: 'auto',
        scrollBehavior: 'smooth',
        // transition styles
        position: 'relative',
        ...scanDestinationSectionStyles,
        ...codeDesignSectionStyles,
        ...authSectionStyles
      }}
      ref={rootContainerRef}
      id={SCROLL_CONTAINER_ID}
    >
      <TransitionGroup component={null}>
        {displayScanDestination && (
          <CSSTransition
            timeout={TRANSITION_DURATION}
            classNames={LoggedOutGeneratorSection.ScanDestination}
            nodeRef={scanDestinationRef}
          >
            <Stack
              id={LoggedOutGeneratorSection.ScanDestination}
              ref={scanDestinationRef}
              gap={2}
              sx={theme => ({
                alignItems: {
                  md: 'flex-start'
                },
                ml: 8,
                mr: { xs: 8, xl: 0 },
                pt: {
                  xs: designFirst ? 5 : 'unset',
                  md: designFirst ? 'unset' : 10
                },
                pb: 32.5,
                width: {
                  lg: '100%'
                },
                maxWidth: {
                  md: theme.spacing(136)
                }
              })}
            >
              <ScanDestination />
            </Stack>
          </CSSTransition>
        )}

        {displayCodeDesign && (
          <CSSTransition
            timeout={TRANSITION_DURATION}
            classNames={LoggedOutGeneratorSection.CodeDesign}
            nodeRef={codeDesignContainerRef}
          >
            <Stack
              id={LoggedOutGeneratorSection.CodeDesign}
              ref={codeDesignContainerRef}
              gap={2}
              alignItems="flex-start"
              sx={{
                ml: 8,
                mr: {
                  xs: 8,
                  xl: 0
                },
                pt: {
                  xs: designFirst ? 5 : 'unset',
                  md: designFirst ? 2 : 'unset'
                },
                pb: {
                  xs: 20,
                  md: 40
                }
              }}
            >
              <CodeDesign ref={codeDesignRefs} />
            </Stack>
          </CSSTransition>
        )}

        {displayAuthForm && (
          <CSSTransition
            timeout={TRANSITION_DURATION}
            classNames={LoggedOutGeneratorSection.SignUp}
            nodeRef={authRef}
          >
            <Stack
              id={LoggedOutGeneratorSection.SignUp}
              ref={authRef}
              gap={2}
              alignItems="flex-start"
              sx={{
                ml: 8,
                mr: { xs: 8, md: 6, xl: 0 },
                pb: {
                  xs: 5,
                  lg: 13
                }
              }}
            >
              <AuthSection />
            </Stack>
          </CSSTransition>
        )}
      </TransitionGroup>
    </Stack>
  )
}
